import { GridColDef } from '@mui/x-data-grid';
import { CpgRetailer } from '../../api/schema';
import { formatters } from '@utils/formattersV2';
import { retailerLabel } from '../../domain/retailers';
import { formatAveragePurchaseFrequency } from '../../domain/formatters';
import { OptimizationIndexChip } from '@features/campaigns/components/OptimizationIndexChip';
import { SignificanceIcon } from '@components/shared/SignificanceIcon';
import { ConfidenceLevel } from '@features/campaigns/components/ConfidenceLevel';
import { Stack, Typography } from '@mui/material';
import { useFeatures } from '@utils/hooks';
import { HideRetailerToggle } from './HideRetailerToggle';

const DEFAULT_MIN_WIDTH = 150;

export type MinimalCpgRetailer = Pick<
  CpgRetailer,
  | 'id'
  | 'name'
  | 'salesLiftIndex'
  | 'projectedIncrementalSales'
  | 'incrementalSalesLift'
  | 'incrementalSalesLiftConfidenceLevel'
  | 'totalProjectedSales'
  | 'averagePricePerTransaction'
  | 'averageTotalBasketAmount'
  | 'penetration'
  | 'averagePurchaseFrequency'
  | 'salesRate'
  | 'statisticallySignificant'
  | 'hidden'
> & {
  ranking?: number;
};

type Column = GridColDef<MinimalCpgRetailer>;

export const columns = (
  features: ReturnType<typeof useFeatures>,
  showHideRetailerColumn = false
) => {
  return [
    {
      field: 'statisticallySignificant',
      headerName: '',
      flex: 0.2,
      minWidth: 40,
      renderCell: ({ row }) => (
        <SignificanceIcon
          status={
            row.statisticallySignificant ? 'significant' : 'insignificant'
          }
        />
      ),
    },
    ...(showHideRetailerColumn
      ? [
          {
            field: 'hidden',
            headerName: 'Hide Retailer',
            flex: 0.8,
            minWidth: 120,
            align: 'center' as const,
            headerAlign: 'center' as const,
            renderCell: ({ row }: { row: MinimalCpgRetailer }) => (
              <HideRetailerToggle retailerId={row.id} hidden={row.hidden} />
            ),
          },
        ]
      : []),
    {
      field: 'ranking',
      headerName: 'Ranking',
      align: 'right',
      headerAlign: 'right',
      flex: 0.5,
      minWidth: 80,
    },
    {
      field: 'name',
      headerName: 'Retailer',
      flex: 1,
      minWidth: 165,
      valueGetter: value => retailerLabel(value),
    },
    {
      field: 'salesLiftIndex',
      headerName: 'Opt. Index - Lift',
      align: 'center',
      headerAlign: 'center',
      flex: 1,
      minWidth: DEFAULT_MIN_WIDTH,
      valueFormatter: value => formatters.number(value, { decimals: 2 }),
      renderCell: ({ row }) => (
        <OptimizationIndexChip value={row.salesLiftIndex} />
      ),
    },
    {
      field: 'projectedIncrementalSales',
      headerName: 'Proj. Incr. Sales',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: DEFAULT_MIN_WIDTH,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 0 }),
    },
    {
      field: 'incrementalSalesLift',
      headerName: 'Incr. Sales Lift',
      display: 'flex',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: DEFAULT_MIN_WIDTH,
      type: 'number',
      renderCell: ({ row }) => (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography>
            {formatters.lift(row.incrementalSalesLift, { decimals: 1 })}
          </Typography>
          {features.cpgAdditionalMetrics ? (
            <ConfidenceLevel
              value={row.incrementalSalesLiftConfidenceLevel}
              lift={row.incrementalSalesLift}
            />
          ) : null}
        </Stack>
      ),
      valueFormatter: value => formatters.lift(value, { decimals: 1 }),
    },
    {
      field: 'totalProjectedSales',
      headerName: 'Total Proj. Sales',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: DEFAULT_MIN_WIDTH,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 0 }),
    },
    {
      field: 'averagePricePerTransaction',
      headerName: 'Avg. Price per Transaction',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: 220,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 2 }),
    },
    {
      field: 'averageTotalBasketAmount',
      headerName: 'Avg. Total Basket Amount',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: 244,
      valueFormatter: value =>
        formatters.currency(value, { full: true, decimals: 2 }),
    },
    {
      field: 'penetration',
      headerName: 'Penetration',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: DEFAULT_MIN_WIDTH,
      valueFormatter: value => formatters.percentage(value, { decimals: 2 }),
    },
    {
      field: 'averagePurchaseFrequency',
      headerName: 'Avg. Purchase Frequency',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: 242,
      valueFormatter: formatAveragePurchaseFrequency,
    },
    {
      field: 'salesRate',
      headerName: 'Sales Rate',
      align: 'right',
      headerAlign: 'right',
      flex: 1,
      minWidth: DEFAULT_MIN_WIDTH,
      valueFormatter: value => formatters.currency(value, { decimals: 2 }),
    },
  ] as const satisfies readonly Column[];
};
